import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  App as AntdApp,
  Button,
  Flex,
  Form,
  Input,
  Select,
  Typography
} from 'antd'
import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import {
  organizationsUserGroupsCreateMutation,
  organizationsUserGroupsPartialUpdateMutation
} from 'src/api/main'
import type { UserGroup } from 'src/api/main'
import { ResizableDrawer } from 'src/components'
import { useShowApiErrors } from 'src/utils'

import { invalidateWorkspaceUserGroups, useGroupRoleOptions } from '../hooks'

import {
  toGroupMember,
  UserGroupMembersField,
  type GroupMemberValue
} from './UserGroupMembersField'

type Props = {
  workspaceId: number
  open: boolean
  group: UserGroup | null
  onClose: () => void
}

type FormValues = {
  name: string
  users?: GroupMemberValue[]
  roles?: number[]
}

export const UserGroupFormDrawer = ({
  workspaceId,
  open,
  group,
  onClose
}: Props) => {
  const { t } = useTranslation()
  const [form] = Form.useForm<FormValues>()
  const qc = useQueryClient()
  const { message } = AntdApp.useApp()
  const showApiErrors = useShowApiErrors()

  const isEdit = group != null

  const create = useMutation(organizationsUserGroupsCreateMutation())
  const update = useMutation(organizationsUserGroupsPartialUpdateMutation())
  // Not `create.isPending || update.isPending`: those drop as soon as the
  // request answers, while the drawer stays open through the invalidation
  // below — a window in which the button is live again and a second click
  // creates a duplicate group.
  const [isSubmitting, setIsSubmitting] = useState(false)
  const isPending = create.isPending || update.isPending || isSubmitting

  useEffect(() => {
    if (!open) return
    form.setFieldsValue({
      name: group?.name ?? '',
      users: (group?.users ?? []).map(toGroupMember),
      roles: (group?.roles ?? []).map((role) => role.id)
    })
  }, [open, group, form])

  // `users` and `roles` replace the whole set server-side, so both always go
  // out in full. `applications` is never sent — Core derives it from the roles.
  const submit = async (values: FormValues) => {
    const users = (values.users ?? []).map((u) => u.id)
    const roles = values.roles ?? []

    setIsSubmitting(true)
    try {
      if (group) {
        await update.mutateAsync({
          path: { id: group.id },
          body: { name: values.name.trim(), users, roles }
        })
      } else {
        await create.mutateAsync({
          body: {
            name: values.name.trim(),
            organization: workspaceId,
            users,
            roles
          }
        })
      }

      await invalidateWorkspaceUserGroups(qc, workspaceId)
      message.success(
        t(group ? 'users.groups.form.updated' : 'users.groups.form.created')
      )
      onClose()
    } catch (e) {
      showApiErrors(e, form)
    } finally {
      setIsSubmitting(false)
    }
  }

  const {
    roleGroups,
    scopeByRoleId,
    isLoading: rolesLoading,
    truncated: rolesTruncated
  } = useGroupRoleOptions(workspaceId, open)

  // The dropdown shows the scope as a group heading; a selected tag has no
  // heading above it, so it repeats the scope inline.
  const renderRoleLabel = ({
    value,
    label
  }: {
    value?: unknown
    label?: ReactNode
  }) => {
    const scope =
      typeof value === 'number' ? (scopeByRoleId.get(value) ?? null) : undefined

    if (scope === undefined) return label

    return (
      <>
        <Typography.Text type="secondary" style={{ fontSize: 11 }}>
          {scope ?? t('users.groups.organizationScope')}
        </Typography.Text>
        {' · '}
        {label}
      </>
    )
  }

  return (
    <ResizableDrawer
      open={open}
      onClose={() => {
        if (!isPending) onClose()
      }}
      defaultWidth={520}
      storageKey="user-groups-form-drawer-width"
      title={t(
        isEdit ? 'users.groups.form.editTitle' : 'users.groups.form.createTitle'
      )}
      footer={
        <Flex justify="flex-end" gap={8}>
          <Button onClick={onClose} disabled={isPending}>
            {t('users.groups.form.cancel')}
          </Button>
          <Button
            type="primary"
            loading={isPending}
            onClick={() => form.submit()}
          >
            {t('users.groups.form.save')}
          </Button>
        </Flex>
      }
    >
      <Form<FormValues> form={form} layout="vertical" onFinish={submit}>
        <Form.Item
          name="name"
          label={t('users.groups.form.name')}
          rules={[
            { required: true, message: t('users.groups.form.nameRequired') }
          ]}
        >
          <Input placeholder={t('users.groups.form.namePlaceholder')} />
        </Form.Item>

        <Form.Item name="users" extra={t('users.groups.form.membersHint')}>
          <UserGroupMembersField workspaceId={workspaceId} />
        </Form.Item>

        <Form.Item
          name="roles"
          label={t('users.groups.form.roles')}
          extra={
            rolesTruncated
              ? t('users.groups.form.rolesTruncated')
              : t('users.groups.form.rolesHint')
          }
        >
          <Select
            mode="multiple"
            allowClear
            loading={rolesLoading}
            options={roleGroups}
            labelRender={renderRoleLabel}
            optionFilterProp="label"
            placeholder={t('users.groups.form.rolesPlaceholder')}
          />
        </Form.Item>
      </Form>
    </ResizableDrawer>
  )
}
